import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, withDirectives as _withDirectives, createVNode as _createVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "panel" }

import { computed, onBeforeMount, Ref, ref, watch } from 'vue';
import { useGettext } from '@jshmrtn/vue3-gettext';
import { useStore } from 'vuex';
import { AppStore } from '@/store';
import auth from '@/core/Auth';
import { ApiParams } from '@/core/http/ApiResource';
import PageLayout from '@/core/components/PageLayout.vue';
import AppTabs from '@/core/components/AppTabs.vue';
import ResourceTable from '@/core/components/resource/ResourceTable.vue';
import Column from '@/core/components/resource/Column';
import ActionColumn from '@/core/components/resource/ActionColumn';
import { TicketStub, useTicket } from '@/modules/ticket/Ticket';
import { TicketStatus } from '@/modules/ticket_status/TicketStatus';
import { TicketCategory } from '@/modules/ticket_category/TicketCategory';
import { getValueInUserLanguage } from '@/core/languageContent';
import { dateTime } from '@/core/date';
import Filter from '@/core/components/resource/Filter';
import { LinkBuilder, SelectOption, TabId } from '@/core/common';


export default /*#__PURE__*/_defineComponent({
  __name: 'TicketIndex',
  setup(__props) {

const resource = useTicket();
const store = useStore<AppStore>();
const { $gettext } = useGettext();
const defaultParams = ref<ApiParams>({});
const ticketToDelete = ref<TicketStub>();
const ticketStatusOptions = computed<TicketStatus[]>(() => store.getters.ticketStatuses);
const ticketCategoryOptions = computed<TicketCategory[]>(() => store.getters.ticketCategories);
const tabs = ref<TabId[]>([
	{ id: 'current', name: $gettext('Aktuellt') },
	{ id: 'ongoing', name: $gettext('Pågående') },
	{ id: 'done', name: $gettext('Avslutade') },
]);
const currentTab = ref<string>('current');

onBeforeMount(async () => {
	await store.dispatch('loadTicketStatuses');
	await store.dispatch('loadTicketCategories');
});

watch(currentTab, (newTab) => {
	if (newTab === 'current') {
		defaultParams.value.state = 'ONGOING,UNREAD';
		return;
	}

	defaultParams.value.state = newTab.toUpperCase();
}, {
	immediate: true,
});

const getStatusName = (t: TicketStub) => {
	const option = ticketStatusOptions.value.find(ts => ts.id === t.status?.id)?.content;
	if (!option) {
		return '-';
	}

	return getValueInUserLanguage(option) || '';
};

const getCategoryName = (t: TicketStub) => {
	const option = ticketCategoryOptions.value.find(tc => tc.id === t.category?.id)?.content;
	if (!option) {
		return '-';
	}

	return getValueInUserLanguage(option) || '';
};

const columns = [
	new Column<TicketStub>($gettext('Skapad'), 'created_time', 't.created_time')
		.withFormatter(t => dateTime(t.created_time)),
	new Column<TicketStub>($gettext('Ärende'), 'id', 't.id', '+'),
	new Column<TicketStub>($gettext('Status'), 'status')
		.withFormatter(getStatusName),
	new Column<TicketStub>($gettext('Kategori'), 'category')
		.withFormatter(getCategoryName),
	new ActionColumn<TicketStub>()
		.withEditLink((t) => ({ name: 'ticket.view', params: { id: t.id } }))
		.withDeleteLink((t: TicketStub) => {
			ticketToDelete.value = t;
		}),
];

if (store.state.authenticatedUser?.law_firm) {
	columns.splice(
		4,
		0,
		new Column<TicketStub>($gettext('Handläggare'), 'worker')
			.withFormatter(t => t.worker?.name || '-'),
	);
	columns.splice(
		5,
		0,
		new Column<TicketStub>($gettext('Kund'), 'customer')
			.withFormatter(t => t.customer?.name || '-'),
	);
}

if (auth.can('TICKET_ASSIGN')) {
	tabs.value.splice(1, 0, {
		id: 'unread',
		name: $gettext('Ej hanterade'),
	});
}

const ticketStatusFilterOptions: Ref<SelectOption[]> = computed(
	() => [{
		value: '',
		label: $gettext('Alla'),
	}, ...ticketStatusOptions.value.map((ts: TicketStatus) => ({
		value: ts.id,
		label: getValueInUserLanguage(ts.content) || '',
	}))],
);

const filters = [
	new Filter('select', $gettext('Status'), 'ticket_status', ticketStatusFilterOptions),
];

const rowLink: LinkBuilder<TicketStub> = (t: TicketStub) => ({
	name: 'ticket.view',
	params: { id: t.id },
});

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _directive_translate = _resolveDirective("translate")!

  return (_openBlock(), _createBlock(PageLayout, {
    "page-title": _unref($gettext)('Ärenden')
  }, {
    "header-actions": _withCtx(() => [
      _withDirectives((_openBlock(), _createBlock(_component_RouterLink, {
        to: {name: 'ticket.create'},
        class: "button"
      }, {
        default: _withCtx(() => [
          _createTextVNode(" Nytt ärende ")
        ]),
        _: 1
      }, 8, ["to"])), [
        [_directive_translate]
      ])
    ]),
    default: _withCtx(() => [
      _createVNode(AppTabs, {
        tabs: tabs.value,
        current: currentTab.value,
        onOpen: _cache[0] || (_cache[0] = ($event: any) => (currentTab.value = $event))
      }, null, 8, ["tabs", "current"]),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(ResourceTable, {
          "to-delete": ticketToDelete.value,
          "onUpdate:toDelete": _cache[1] || (_cache[1] = ($event: any) => ((ticketToDelete).value = $event)),
          columns: columns,
          "default-params": defaultParams.value,
          resource: _unref(resource),
          filters: filters,
          "row-link": rowLink,
          "delete-name-key": "id",
          "delete-title": _unref($gettext)('Ta bort ärende')
        }, null, 8, ["to-delete", "default-params", "resource", "delete-title"])
      ])
    ]),
    _: 1
  }, 8, ["page-title"]))
}
}

})